<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';

import mfaAPI from 'dashboard/api/mfa';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';

const props = defineProps({
  isMfaAvailable: {
    type: Boolean,
    default: true,
  },
});

const router = useRouter();
const route = useRoute();

const mfaEnabled = ref(false);
const isLoading = ref(true);
const isSavingPhone = ref(false);
const isFeatureAvailable = ref(null);
const availableMethods = ref([]);
const mfaMethod = ref('authenticator');
const mfaPhoneNumber = ref('');
const whatsappPhoneTitle = 'Telefone para 2FA por WhatsApp';
const whatsappPhoneDescription =
  'Cadastre o número que receberá os códigos de verificação por WhatsApp.';
const whatsappPhoneLabel = 'Telefone';
const whatsappPhonePlaceholder = '11999999999';
const whatsappPhoneSaveLabel = 'Salvar telefone';
const whatsappPhoneSavingLabel = 'Salvando...';

const hasWhatsappSetup = computed(() =>
  availableMethods.value.includes('whatsapp')
);

const effectiveMfaAvailability = () =>
  isFeatureAvailable.value === null
    ? props.isMfaAvailable
    : isFeatureAvailable.value;

const navigateToMfa = () => {
  router.push({
    name: 'profile_settings_mfa',
    params: {
      accountId: route.params.accountId,
    },
  });
};

const loadMfaStatus = async () => {
  isLoading.value = true;
  try {
    const response = await mfaAPI.get();
    mfaEnabled.value = !!response.data?.enabled;
    availableMethods.value = response.data?.available_methods || [];
    mfaMethod.value = response.data?.method || 'authenticator';
    mfaPhoneNumber.value = response.data?.phone_number || '';
    isFeatureAvailable.value = true;
  } catch (error) {
    mfaEnabled.value = false;
    isFeatureAvailable.value = error?.response?.status !== 403;
  } finally {
    isLoading.value = false;
  }
};

const saveWhatsappPhoneNumber = async () => {
  isSavingPhone.value = true;
  try {
    const response = await mfaAPI.updatePreferences({
      method: mfaMethod.value || 'authenticator',
      phone_number: mfaPhoneNumber.value,
    });
    mfaMethod.value = response.data?.method || mfaMethod.value;
    mfaPhoneNumber.value = response.data?.phone_number || mfaPhoneNumber.value;
    useAlert('Telefone de 2FA por WhatsApp salvo.');
  } catch (error) {
    useAlert('Não foi possível salvar o telefone de 2FA por WhatsApp.');
  } finally {
    isSavingPhone.value = false;
  }
};

onMounted(loadMfaStatus);
</script>

<template>
  <div class="space-y-4">
    <div
      class="bg-n-background rounded-xl p-4 border border-n-slate-4 flex items-center justify-between gap-3"
    >
      <div class="flex items-center gap-2">
        <Icon icon="i-lucide-shield-check" class="size-4 text-n-slate-10" />
        <h5 class="text-heading-3 text-n-slate-12">
          {{ $t('MFA_SETTINGS.TITLE') }}
        </h5>
      </div>
      <span
        class="text-xs font-medium px-2 py-1 rounded-full"
        :class="
          !effectiveMfaAvailability()
            ? 'bg-n-amber-3 text-n-amber-11'
            : mfaEnabled
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-slate-3 text-n-slate-11'
        "
      >
        {{
          !effectiveMfaAvailability()
            ? $t('MFA_SETTINGS.DISABLED')
            : mfaEnabled
              ? $t('MFA_SETTINGS.ENABLED')
              : $t('MFA_SETTINGS.DISABLED')
        }}
      </span>
    </div>

    <div
      v-if="!effectiveMfaAvailability()"
      class="bg-n-amber-2 text-n-amber-12 border border-n-amber-6 rounded-xl px-4 py-3 text-sm"
    >
      {{ $t('MFA_SETTINGS.SETUP.ERROR_STARTING') }}
    </div>

    <div
      v-if="effectiveMfaAvailability() && hasWhatsappSetup"
      class="bg-n-background rounded-xl p-4 border border-n-slate-4"
    >
      <div class="flex flex-col gap-3">
        <div class="flex items-center gap-2">
          <Icon icon="i-ri-whatsapp-fill" class="size-4 text-n-slate-10" />
          <h6 class="text-sm font-medium text-n-slate-12">
            {{ whatsappPhoneTitle }}
          </h6>
        </div>
        <p class="text-sm text-n-slate-11">
          {{ whatsappPhoneDescription }}
        </p>
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          <span>{{ whatsappPhoneLabel }}</span>
          <div
            class="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
          >
            <PhoneNumberInput
              v-model="mfaPhoneNumber"
              :placeholder="whatsappPhonePlaceholder"
            />
            <Button
              type="button"
              size="sm"
              :disabled="isLoading || isSavingPhone"
              :label="
                isSavingPhone
                  ? whatsappPhoneSavingLabel
                  : whatsappPhoneSaveLabel
              "
              icon="i-lucide-save"
              class="w-full sm:w-auto"
              @click="saveWhatsappPhoneNumber"
            />
          </div>
        </label>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="bg-n-background rounded-xl p-4 border border-n-slate-4">
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <Icon icon="i-lucide-lock-keyhole" class="size-4 text-n-slate-10" />
            <h6 class="text-sm font-medium text-n-slate-12">
              {{ $t('MFA_SETTINGS.ENABLE_BUTTON') }}
            </h6>
          </div>
          <p class="text-sm text-n-slate-11">
            {{ $t('MFA_SETTINGS.ENHANCE_SECURITY_DESC') }}
          </p>
          <Button
            type="button"
            faded
            teal
            :disabled="isLoading || mfaEnabled || !effectiveMfaAvailability()"
            :label="$t('MFA_SETTINGS.ENABLE_BUTTON')"
            icon="i-lucide-shield-plus"
            @click="navigateToMfa"
          />
        </div>
      </div>

      <div class="bg-n-background rounded-xl p-4 border border-n-slate-4">
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <Icon
              icon="i-lucide-lock-keyhole-open"
              class="size-4 text-n-slate-10"
            />
            <h6 class="text-sm font-medium text-n-slate-12">
              {{ $t('MFA_SETTINGS.MANAGEMENT.DISABLE_MFA') }}
            </h6>
          </div>
          <p class="text-sm text-n-slate-11">
            {{ $t('MFA_SETTINGS.MANAGEMENT.DISABLE_MFA_DESC') }}
          </p>
          <Button
            type="button"
            faded
            ruby
            :disabled="isLoading || !mfaEnabled || !effectiveMfaAvailability()"
            :label="$t('MFA_SETTINGS.MANAGEMENT.DISABLE_BUTTON')"
            icon="i-lucide-shield-off"
            @click="navigateToMfa"
          />
        </div>
      </div>
    </div>
  </div>
</template>
